// Клиент `/api/v1/connections/task-tracker/*` (ADR-0075).
//
// У организации одно подключение трекера задач (`UNIQUE(org_id)`),
// отдельное от подключения репо-хоста Forgejo (`./api`): тот же сервер
// может быть и репо-хостом, и трекером, но токен трекера вводится
// отдельно. Контракт — `backend/modules/task_tracker/{schemas,router}.py`.
//
// PAT никогда не возвращается в response. `credentials` сегодня — всегда
// `{pat}`, провайдер фиксирован (`forgejo`), но тип не завязан на это
// жёстко: он приходит из каталога `/providers`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";
import { buildJsonHeaders, HttpError } from "./api";

export const TASK_TRACKER_URL = "/api/v1/connections/task-tracker";

// Зеркало `DETAIL_HOST_NOT_ALLOWED` из
// `backend/modules/task_tracker/details.py`: по точной строке `detail`
// отличаем `403` allowlist от `403` RBAC.
export const TRACKER_HOST_NOT_ALLOWED_DETAIL = "tracker host not allowed";

// Зеркало `DETAIL_CREDENTIALS_REJECTED` оттуда же: по нему `401` трекера
// отличается от `401` истёкшей сессии Baton.
export const TRACKER_CREDENTIALS_REJECTED_DETAIL =
  "tracker rejected credentials";

export interface TrackerProvider {
  readonly name: string;
}

export interface TrackerConnectionCredentials {
  readonly pat: string;
}

export interface TrackerConnectionBody {
  readonly provider: string;
  readonly host_url: string;
  readonly credentials: TrackerConnectionCredentials;
}

export interface TrackerConnection {
  readonly connected: boolean;
  readonly provider: string | null;
  readonly host_url: string | null;
  readonly has_credentials: boolean;
  readonly principal_login: string | null;
}

export interface TrackerVerifyResult {
  readonly principal_login: string;
}

// Зеркало `TrackerStatusReason` (`backend/modules/task_tracker/schemas.py`).
export type TrackerStatusReason =
  | "host_not_allowed"
  | "credentials_missing"
  | "credentials_rejected"
  | "unreachable"
  | "provider_unknown";

export interface TrackerStatus {
  readonly connected: boolean;
  readonly reachable: boolean | null;
  readonly principal_login: string | null;
  readonly principal_matches: boolean | null;
  readonly reason: TrackerStatusReason | null;
}

/**
 * HTTP-ошибка `task-tracker`-ручек с `detail` тела ответа. Зеркало
 * `organizationSettings.HttpError` / `repositorySettings.HttpError`:
 * `detail` — обобщённая константа backend'а (`details.py`), никогда не
 * несёт кредов или `host_url` (leak-discipline модуля).
 */
export class TrackerRequestError extends HttpError {
  public readonly detail: string | null;
  public constructor(status: number, message: string, detail: string | null) {
    super(status, message);
    this.name = "TrackerRequestError";
    this.detail = detail;
  }
}

// Бросить TrackerRequestError с `detail` тела отказа; без строкового
// `detail` вызывающая сторона покажет generic-сообщение по коду.
async function throwTrackerError(
  response: Response,
  label: string,
): Promise<never> {
  const detail = await readErrorDetail(response);
  throw new TrackerRequestError(
    response.status,
    `${label}: HTTP ${String(response.status)}`,
    detail,
  );
}

export async function getTrackerProviders(): Promise<
  readonly TrackerProvider[]
> {
  const response = await authedFetch(`${TASK_TRACKER_URL}/providers`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    await throwTrackerError(response, "getTrackerProviders");
  }
  return (await response.json()) as readonly TrackerProvider[];
}

export async function getTrackerConnection(): Promise<TrackerConnection> {
  const response = await authedFetch(TASK_TRACKER_URL, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    await throwTrackerError(response, "getTrackerConnection");
  }
  return (await response.json()) as TrackerConnection;
}

export async function getTrackerStatus(): Promise<TrackerStatus> {
  const response = await authedFetch(`${TASK_TRACKER_URL}/status`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    await throwTrackerError(response, "getTrackerStatus");
  }
  return (await response.json()) as TrackerStatus;
}

/**
 * Проверить креды без сохранения (`POST /verify`).
 *
 * `skipAutoLogout`: `401` здесь значит «трекер отверг переданный
 * токен», не «сессия Baton истекла» — выкидывать пользователя из
 * Baton на эту ошибку нельзя.
 */
export async function verifyTracker(
  body: TrackerConnectionBody,
): Promise<TrackerVerifyResult> {
  const response = await authedFetch(`${TASK_TRACKER_URL}/verify`, {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    await throwTrackerError(response, "verifyTracker");
  }
  return (await response.json()) as TrackerVerifyResult;
}

/**
 * Подключить трекер (проба + сохранение, `PUT /`). Тот же
 * `skipAutoLogout`, что и у `verifyTracker` — причина та же.
 */
export async function putTrackerConnection(
  body: TrackerConnectionBody,
): Promise<TrackerConnection> {
  const response = await authedFetch(TASK_TRACKER_URL, {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
    skipAutoLogout: true,
  });
  if (!response.ok) {
    await throwTrackerError(response, "putTrackerConnection");
  }
  return (await response.json()) as TrackerConnection;
}

/** Отключить трекер (`DELETE /`). Backend отвечает `204` безусловно. */
export async function deleteTrackerConnection(): Promise<void> {
  const response = await authedFetch(TASK_TRACKER_URL, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    await throwTrackerError(response, "deleteTrackerConnection");
  }
}
